import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useConnectAcomMutation, useLazyGetAcomInfoQuery } from '../apiRtk/be4feApi';
import { TYPE_DESCRIPTOR } from '../modules/notifications';
import { addSnackbarError } from '../modules/notifications/actions';
import messages from './messages';

export const ACOM_CONNECT_POLL_INTERVAL_MS = 2000;
export const ACOM_CONNECT_TIMEOUT_MS = 30000;

export interface IOpenAccessCommanderParams {
  companyId: number;
  siteId: number;
}

export interface IUseAcomConnect {
  openAccessCommander: (params: IOpenAccessCommanderParams) => void;
  isConnecting: boolean;
}

export function useAcomConnect(): IUseAcomConnect {
  const dispatch = useDispatch();
  const [connectAcom] = useConnectAcomMutation();
  const [getAcomInfo] = useLazyGetAcomInfoQuery();
  const [isConnecting, setIsConnecting] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isMountedRef = useRef(true);
  const isConnectingRef = useRef(false);
  const newTabRef = useRef<Window | null>(null);

  const clearPollTimer = () => {
    if (pollTimerRef.current !== undefined) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = undefined;
    }
  };

  const clearTimers = () => {
    clearPollTimer();
    if (timeoutTimerRef.current !== undefined) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = undefined;
    }
  };

  const closeNewTab = () => {
    if (newTabRef.current && !newTabRef.current.closed) {
      newTabRef.current.close();
    }
    newTabRef.current = null;
  };

  const stopConnecting = () => {
    clearTimers();
    isConnectingRef.current = false;
    if (isMountedRef.current) {
      setIsConnecting(false);
    }
  };

  const openInNewTab = (url: string) => {
    const tab = newTabRef.current;
    if (!tab || tab.closed) {
      newTabRef.current = null;
      return;
    }
    tab.location.href = url;
    tab.opener = null;
    newTabRef.current = null;
  };

  const armTimeout = () => {
    timeoutTimerRef.current = setTimeout(() => {
      stopConnecting();
      closeNewTab();
      dispatch(addSnackbarError(messages.AcomConnectTimeout, TYPE_DESCRIPTOR));
    }, ACOM_CONNECT_TIMEOUT_MS);
  };

  const startPolling = (companyId: number, siteId: number) => {
    clearPollTimer();

    pollTimerRef.current = setInterval(() => {
      getAcomInfo({ companyId, siteId })
        .unwrap()
        .then((info) => {
          if (!isMountedRef.current) {
            return;
          }
          if (info?.connectionUrl) {
            stopConnecting();
            openInNewTab(info.connectionUrl);
          }
        })
        .catch(() => {});
    }, ACOM_CONNECT_POLL_INTERVAL_MS);
  };

  const openAccessCommander = ({ companyId, siteId }: IOpenAccessCommanderParams) => {
    if (isConnectingRef.current) {
      return;
    }

    isConnectingRef.current = true;
    setIsConnecting(true);
    newTabRef.current = window.open('', '_blank');

    armTimeout();

    getAcomInfo({ companyId, siteId })
      .unwrap()
      .then((fresh) => {
        if (!isMountedRef.current) {
          isConnectingRef.current = false;
          closeNewTab();
          return;
        }

        if (fresh?.connectionUrl) {
          stopConnecting();
          openInNewTab(fresh.connectionUrl);
          return;
        }

        connectAcom({ companyId, siteId })
          .unwrap()
          .then(() => {
            if (!isMountedRef.current) {
              isConnectingRef.current = false;
              closeNewTab();
              return;
            }
            startPolling(companyId, siteId);
          })
          .catch(() => {
            stopConnecting();
            closeNewTab();
            if (isMountedRef.current) {
              dispatch(addSnackbarError(messages.AcomConnectError, TYPE_DESCRIPTOR));
            }
          });
      })
      .catch(() => {
        stopConnecting();
        closeNewTab();
        if (isMountedRef.current) {
          dispatch(addSnackbarError(messages.AcomConnectError, TYPE_DESCRIPTOR));
        }
      });
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      isConnectingRef.current = false;
      clearTimers();
      closeNewTab();
    };
  }, []);

  return { isConnecting, openAccessCommander };
}
